import React, { useRef } from 'react';
import clsx from 'clsx';

import {
  type MButtonProps,
  MButtonType,
  MButtonSize,
  MButtonIconPlacement,
  MButtonIconClass,
  DEFAULT_OPEN_IN_NEW_TAB_TEXT,
} from './MButton.types';
import * as Styled from './MButton.styles';

import { AIcon, AIconSize } from '../../atoms/AIcon';
import { useTracking } from '../../hooks';

const COMPONENT_NAME = 'm-web-ui-kit-MButton';
const TEST_ID = 'web-ui-kit-MButton';

const MButtonContent: React.FC<{
  buttonType: MButtonType;
  size: MButtonSize;
  iconPlacement: MButtonIconPlacement;
  iconClass?: string;
  aIcon?: { size?: string; icon: string };
  isExternalLink: boolean;
  openInANewTabText: string;
  children: React.ReactNode;
}> = ({ buttonType, size, iconPlacement, iconClass, aIcon, isExternalLink, openInANewTabText, children }) => {
  let resolvedIconSize: AIconSize = AIconSize.S;
  if (size === MButtonSize.L && buttonType !== MButtonType.Tertiary) {
    resolvedIconSize = AIconSize.L;
  }

  if (aIcon?.size) {
    resolvedIconSize = (aIcon.size as AIconSize) || AIconSize.S;
  }

  const shouldShowLeadingIcon = iconPlacement === MButtonIconPlacement.Leading;
  const shouldShowTrailingIcon =
    buttonType === MButtonType.Tertiary
      ? iconPlacement !== MButtonIconPlacement.Leading
      : iconPlacement === MButtonIconPlacement.Trailing;

  const defaultLeadingIconClass =
    buttonType === MButtonType.Tertiary ? MButtonIconClass.BackArrow : MButtonIconClass.Search;
  const defaultTrailingIconClass =
    buttonType === MButtonType.Tertiary ? MButtonIconClass.ForwardArrow : MButtonIconClass.ArrowRight;

  const leadingIconClass = iconClass ?? (aIcon?.icon ? aIcon.icon : defaultLeadingIconClass);
  const trailingIconClass = iconClass ?? (aIcon?.icon ? aIcon.icon : defaultTrailingIconClass);

  return (
    <>
      {!isExternalLink && shouldShowLeadingIcon && (
        <AIcon icon={leadingIconClass} size={resolvedIconSize} data-testid={`${TEST_ID}-leading-icon`} />
      )}
      <Styled.Label className="btn-label">{children}</Styled.Label>
      {isExternalLink ? (
        <>
          {' ' /* space added for better readability */}
          <AIcon
            icon={MButtonIconClass.ExternalArrow}
            size={resolvedIconSize}
            data-testid={`${TEST_ID}-trailing-icon`}
            aria-label={openInANewTabText}
          />
        </>
      ) : (
        shouldShowTrailingIcon && (
          <AIcon icon={trailingIconClass} size={resolvedIconSize} data-testid={`${TEST_ID}-trailing-icon`} />
        )
      )}
    </>
  );
};

const MButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, MButtonProps>(
  (
    {
      buttonText,
      children,
      buttonType: buttonTypeProp,
      variant: variantProp,
      size = MButtonSize.M,
      iconPlacement = MButtonIconPlacement.None,
      iconClass,
      aIcon,
      isExternal = false,
      openInANewTab = false,
      disabled = false,
      type = 'button',
      target,
      href,
      ctaLink,
      className,
      trackingProperties,
      custom_click_track_value,
      preTrackVal,
      openInANewTabText = DEFAULT_OPEN_IN_NEW_TAB_TEXT,
      ...rest
    },
    ref
  ) => {
    const buttonType = buttonTypeProp ?? variantProp ?? MButtonType.Primary;

    const renderAsLink = !!ctaLink || !!href;
    const extTargetVal = '_blank';
    const isExternalLink =
      renderAsLink &&
      (openInANewTab === true || openInANewTab === 'true' || isExternal === true || target === extTargetVal);

    const buttonTypeClass = {
      'm-button-primary': buttonType === MButtonType.Primary,
      'm-button-secondary': buttonType === MButtonType.Secondary,
      'm-link-tertiary-button': buttonType === MButtonType.Tertiary && !isExternalLink,
      'm-link-tertiary-button-external': buttonType === MButtonType.Tertiary && isExternalLink,
    };

    const buttonSizeClass =
      buttonType === MButtonType.Tertiary
        ? {}
        : {
            'm-button-s': size === MButtonSize.S,
            'm-button-m': size === MButtonSize.M,
            'm-button-l': size === MButtonSize.L,
          };

    const iconPlacementClass = {
      'icon-leading': iconPlacement === MButtonIconPlacement.Leading && !isExternalLink,
      'icon-trailing': iconPlacement === MButtonIconPlacement.Trailing || isExternalLink,
    };

    const mergedClassName = clsx(className, buttonTypeClass, buttonSizeClass, iconPlacementClass, { disabled });

    const polymorphicProps = renderAsLink
      ? {
          href: ctaLink || href,
          target: isExternalLink ? extTargetVal : target,
          ...(disabled ? { 'aria-disabled': true } : {}),
        }
      : { type, disabled };

    const defaultRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
    const elementRef = (ref ?? defaultRef) as React.RefObject<HTMLAnchorElement | HTMLButtonElement>;

    useTracking({
      ref: elementRef,
      trackingProperties,
      isExternal: isExternalLink,
      custom_click_track_value,
      preTrackVal,
      text: buttonText,
    });

    const sharedWrapperProps = {
      'data-component-name': COMPONENT_NAME,
      'data-testid': TEST_ID,
      className: mergedClassName,
      $buttonType: buttonType,
      $size: size,
      ...polymorphicProps,
      ...rest,
    };

    const buttonContent = (
      <MButtonContent
        isExternalLink={isExternalLink}
        buttonType={buttonType}
        size={size}
        iconPlacement={iconPlacement}
        iconClass={iconClass}
        aIcon={aIcon}
        openInANewTabText={openInANewTabText}
      >
        {buttonText}
        {typeof children === 'string' ? ` ${children}` : children}
      </MButtonContent>
    );

    if (renderAsLink) {
      return (
        <Styled.Wrapper
          {...sharedWrapperProps}
          role="link"
          ref={elementRef as React.Ref<HTMLAnchorElement>}
          as="a"
          onKeyDown={e => {
            if ((buttonType !== MButtonType.Tertiary && e.key === ' ') || e.key === 'Spacebar') {
              e.preventDefault();
              window.location.href = (href || ctaLink) ?? '';
            }
          }}
        >
          {buttonContent}
        </Styled.Wrapper>
      );
    }

    return (
      <Styled.Wrapper {...sharedWrapperProps} role="button" ref={elementRef as React.Ref<HTMLButtonElement>}>
        {buttonContent}
      </Styled.Wrapper>
    );
  }
);

export {
  MButton,
  MButtonIconPlacement,
  MButtonIconClass,
  MButtonSize,
  MButtonType,
  DEFAULT_OPEN_IN_NEW_TAB_TEXT,
  TEST_ID as M_BUTTON_TEST_ID,
};
